import type { useAnalyticsReporting } from '@/api/useAnalyticsReporting'
import { CloseCircleFilled, CloseCircleOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { addCommasToNumber, BBDTag, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Avatar, message, Tooltip } from 'antd'
import { useState } from 'react'

type Page = 'tax-valuations' | 'bol-transactions'

type Issue = {
  message: string
  severity: string
}

const COLUMNS = {
  status: {
    headerName: 'Status',
    field: 'status',
    minWidth: 120,
    cellRenderer: ({ value }) => {
      const status = value.toLowerCase()
      return (
        <BBDTag success={status === 'completed'} warning={status === 'pending'} error={status === 'error'}>
          {value.toLowerCase()}
        </BBDTag>
      )
    },
  },
  issues: { headerName: 'Issues', field: 'issues', cellRenderer: IssuesCellRenderer },
  order_number: { headerName: 'Order #', field: 'order_number' },
  purchase_order_number: { headerName: 'Purchase Order #', field: 'purchase_order_number' },
  bol_number: { headerName: 'BOL #', field: 'bol_number' },
  bol_date: {
    headerName: 'BOL Date',
    field: 'bol_date',
    ...defaultDateDef('bol_date'),
  },
  terminal: { headerName: 'Terminal', field: 'terminal' },
  supplier: { headerName: 'Supplier', field: 'supplier' },
  product: {
    headerName: 'Product',
    field: 'product',
    minWidth: 150,
    cellRenderer: ({ value }) =>
      value ? (
        <BBDTag className='text-ellipsis' style={{ borderRadius: 8 }}>
          {value}
        </BBDTag>
      ) : (
        ''
      ),
  },
  contract_number: { headerName: 'Contract #', field: 'contract_number' },
  destination: { headerName: 'Destination', field: 'destination' },
  delivery_date: {
    headerName: 'Delivery Date',
    field: 'delivery_date',
    ...defaultDateDef('delivery_date'),
  },
  gross_volume: {
    headerName: 'Gross Transaction Volume',
    field: 'gross_volume',
    cellRenderer: ({ value }) => <Texto align='right'>{addCommasToNumber(value)}</Texto>,
  },
  net_volume: {
    headerName: 'Net Transaction Volume',
    field: 'net_volume',
    cellRenderer: ({ value }) => <Texto align='right'>{addCommasToNumber(value)}</Texto>,
  },
  transaction_amount: {
    headerName: 'Transaction Amount',
    field: 'transaction_amount',
    cellRenderer: PriceCellRenderer,
  },
  country_code: { headerName: 'Country Code', field: 'country_code' },
  jurisdiction: { headerName: 'Jurisdiction', field: 'jurisdiction' },
  local_jurisdiction: { headerName: 'Local Jurisdiction', field: 'local_jurisdiction' },
  product_category: { headerName: 'Product Category', field: 'product_category', initialHide: true },
  taxing_level: { headerName: 'Taxing Level', field: 'taxing_level', initialHide: true },
  tax_type: { headerName: 'Tax Type', field: 'tax_type', initialHide: true },
  rate_type: { headerName: 'Rate Type', field: 'rate_type', initialHide: true },
  rate_subtype: { headerName: 'Rate Subtype', field: 'rate_subtype', initialHide: true },
  calculation_type: { headerName: 'Calculation Type', field: 'calculation_type', initialHide: true },
  rate: { headerName: 'Tax Rate', field: 'rate', cellRenderer: ({ value }) => <Texto align='right'>{value}</Texto> },
  tax_quantity: {
    headerName: 'Tax Quantity',
    field: 'tax_quantity',
    cellRenderer: ({ value }) => <Texto align='right'>{addCommasToNumber(value)}</Texto>,
  },
  amount: {
    headerName: 'Tax Amount',
    field: 'amount',
    cellRenderer: ({ value }) => <Texto align='right'>{addCommasToNumber(value)}</Texto>,
  },
  tax_exempt_ind: {
    headerName: 'Tax Exemption',
    field: 'tax_exempt_ind',
    cellRenderer: ({ value }) => (value ? 'Yes' : ''),
  },
  deferred_ind: {
    headerName: 'Deferred Tax',
    field: 'deferred_ind',
    cellRenderer: ({ value }) => (value ? 'Yes' : ''),
  },
  sales_tax_base_amount: {
    headerName: 'Sales Tax Base Amount',
    field: 'sales_tax_base_amount',
    cellRenderer: ({ value }) => <Texto align='right'>{addCommasToNumber(value)}</Texto>,
  },
  license_number: { headerName: 'License Number', field: 'license_number' },
  rate_description: { headerName: 'Tax Description', field: 'rate_description' },
  currency: { headerName: 'Currency', field: 'currency' },
  unit_of_measure: { headerName: 'Unit of Measure', field: 'unit_of_measure' },
  subtotal_indicator: { headerName: 'Subtotal Indicator', field: 'subtotal_indicator', initialHide: true },
  status_code: {
    headerName: 'Status Code',
    field: 'status_code',
    initialHide: true,
    cellRenderer: 'numberCell',
  },
  quantity_indicator: { headerName: 'Quantity Indicator', field: 'quantity_indicator', initialHide: true },
  estimate_actual: {
    headerName: 'Estimate/Actual',
    field: 'estimate_actual',
  },
  transaction_type: {
    headerName: 'Transaction Type',
    field: 'transaction_type',
  },
  billable_volume: {
    headerName: 'Billable Volume',
    field: 'billable_volume',
    cellRenderer: ({ value }) => <Texto align='right'>{addCommasToNumber(value)}</Texto>,
  },
  billable_transaction_date: {
    headerName: 'Billable Transaction Date',
    field: 'billable_transaction_date',
    ...defaultDateDef('billable_transaction_date', formatters.DATE, true),
  },
  per_unit_price: {
    headerName: 'Per Unit Price',
    field: 'per_unit_price',
    cellRenderer: PriceCellRenderer,
  },
  error_description: {
    headerName: 'Error Description',
    field: 'error_description',
  },
  last_valuation_date: {
    headerName: 'Last Valuation Date',
    field: 'last_valuation_date',
    initialHide: true,
    ...defaultDateDef('last_valuation_date', formatters.SHORT_DATE_TIME),
  },
}

const COLUMN_SETS = {
  'tax-valuations': [
    'status',
    'issues',
    'order_number',
    'purchase_order_number',
    'bol_number',
    'bol_date',
    'terminal',
    'supplier',
    'product',
    'contract_number',
    'destination',
    'delivery_date',
    'gross_volume',
    'net_volume',
    'estimate_actual',
    'transaction_amount',
    'country_code',
    'jurisdiction',
    'local_jurisdiction',
    'product_category',
    'taxing_level',
    'tax_type',
    'rate_type',
    'rate_subtype',
    'calculation_type',
    'rate',
    'tax_quantity',
    'amount',
    'tax_exempt_ind',
    'deferred_ind',
    'sales_tax_base_amount',
    'license_number',
    'rate_description',
    'currency',
    'unit_of_measure',
    'subtotal_indicator',
    'status_code',
    'quantity_indicator',
  ],
  'bol-transactions': [
    'order_number',
    'purchase_order_number',
    'bol_number',
    'bol_date',
    'terminal',
    'supplier',
    'product',
    'contract_number',
    'destination',
    'delivery_date',
    'gross_volume',
    'net_volume',
    'estimate_actual',
    'status',
    'transaction_type',
    'billable_volume',
    'billable_transaction_date',
    'per_unit_price',
    'transaction_amount',
    'unit_of_measure',
    'currency',
    'error_description',
    'last_valuation_date',
  ],
}

export function getColumnDefs(
  page: Page,
  recalculate:
    | ReturnType<typeof useAnalyticsReporting>['useRecalculateTaxesMutation']
    | ReturnType<typeof useAnalyticsReporting>['useRecalculateBOLsMutation']
) {
  const columns = COLUMN_SETS[page].map((column) => COLUMNS[column])
  const type = page === 'tax-valuations' ? 'Taxes' : 'BOLs'
  columns.push({
    minWidth: 170,
    headerName: 'Actions',
    field: 'actions',
    cellRenderer: ({ data }) => <ActionsCellRenderer data={data} recalculate={recalculate} type={type} />,
  })
  return columns
}

function ActionsCellRenderer({ data, recalculate, type }) {
  const [loading, setLoading] = useState(false)
  return (
    <Horizontal>
      <GraviButton
        onClick={() => {
          setLoading(true)
          recalculate
            .mutateAsync({ order_number: data.order_number })
            .then(() => message.success(`${type} Recalculated.`))
            .catch(() => message.error(`Failed to recalculate ${type.toLowerCase()}.`))
            .finally(() => setLoading(false))
        }}
        loading={loading}
        size='small'
        className='mr-3'
        buttonText={`Recalculate ${type}`}
        icon={loading ? <LoadingOutlined /> : <ReloadOutlined className='pr-1 ml-1' />}
      />
    </Horizontal>
  )
}

function IssuesCellRenderer({ value }: { value: Issue[] }) {
  if (!value) return null
  return (
    <Horizontal gap={8}>
      <Avatar.Group size={26}>
        {value.map((issue, index) => {
          const isError = issue.severity === 'Critical'
          return (
            <Tooltip key={index} title={issue.message}>
              <Avatar
                icon={
                  isError ? (
                    <CloseCircleFilled color='red' style={{ fontSize: 20 }} />
                  ) : (
                    <CloseCircleOutlined style={{ fontSize: 20 }} />
                  )
                }
                style={{
                  backgroundColor: isError ? 'var(--theme-error)' : 'var(--theme-warning)',
                }}
              />
            </Tooltip>
          )
        })}
      </Avatar.Group>
    </Horizontal>
  )
}

function PriceCellRenderer({ value }) {
  const { formatCurrency } = useFormatter()
  return <Texto align='right'>{value ? formatCurrency(value, true, 4) : ''}</Texto>
}
